import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import './HowWeReview.css';

const criteria = [
  {
    num: '01',
    weight: '25%',
    title: 'Licens & Regulering',
    icon: '🏛️',
    desc: 'Alle casinoer skal have en gyldig licens fra Spillemyndigheden for at blive omtalt på vores platform. Vi verificerer licenser direkte via Spillemyndighedens offentlige register.',
    points: [
      'Gyldig dansk licens fra Spillemyndigheden',
      'Ingen aktive tilsynssager eller bøder',
      'Klar ansvarlighedsstruktur og operatøridentitet',
      'ROFUS-integration verificeret',
    ]
  },
  {
    num: '02',
    weight: '20%',
    title: 'Spiludvalg & Softwarekvalitet',
    icon: '🎮',
    desc: 'Vi vurderer bredden og dybden af spilsortimentet, herunder slots, live casino, bordspil og eventuelle sportsmarkeder. Vi kigger på softwareleverandørernes kvalitet og variationen i spillene.',
    points: [
      'Antal og variation af spillemaskiner',
      'Kvaliteten af live casino-sektionen',
      'Anerkendte softwareudbydere (NetEnt, Evolution, Playtech etc.)',
      'Mobiloptimering og app-tilgængelighed',
    ]
  },
  {
    num: '03',
    weight: '20%',
    title: 'Betalinger & Udbetalinger',
    icon: '💳',
    desc: 'Vi tester og dokumenterer betalingsprocessen fra ind- til udbetaling. Vi er særligt opmærksomme på udbetalingshastighed, gebyrer og tilgængelige betalingsmetoder.',
    points: [
      'Tilgængelige danske betalingsmetoder (MobilePay, Neteller, bankoverførsel)',
      'Udbetalingshastighed og maksimale grænser',
      'Eventuelle gebyrer ved ind- og udbetaling',
      'Verifikationsprocessens grundighed og hastighed',
    ]
  },
  {
    num: '04',
    weight: '20%',
    title: 'Ansvarligt Spil & Spillerbeskyttelse',
    icon: '🛡️',
    desc: 'Ansvarligt spil vejer tungere i vores vurdering end hos mange andre platforme. Vi kigger på, hvilke redskaber casinoet tilbyder spillerne til at kontrollere og begrænse deres spil.',
    points: [
      'Tids- og beløbsgrænser tilgængeligt i konto-indstillinger',
      'Mulighed for selvpause og selvudelukkelse',
      'Tydelig integration med ROFUS',
      'Proaktive råd og informationsmaterialer om ansvarligt spil',
    ]
  },
  {
    num: '05',
    weight: '15%',
    title: 'Brugeroplevelse & Support',
    icon: '⭐',
    desc: 'En god spiloplevelse kræver en intuitiv platform og tilgængelig, kompetent support. Vi tester kundeservicens svartider og kvalitet, typisk via chat og e-mail.',
    points: [
      'Navigationsstruktur og brugervenlighed',
      'Responsivt design og mobiloplevelse',
      'Svartid og kvalitet af kundesupport',
      'Dansk sproglig support tilgængelighed',
    ]
  },
];

export default function HowWeReview() {
  return (
    <>
      <Helmet>
        <title>Vores Anmeldelsesmetode – TopCasinoDansk</title>
        <meta name="description" content="Forstå, hvordan TopCasinoDansk vurderer og anmelder online casinoer. Vores metode er transparent, uafhængig og baseret på klare kriterier." />
      </Helmet>

      <PageHero
        title="Vores Anmeldelsesmetode"
        subtitle="Transparens er grundlaget for vores troværdighed. Her forklarer vi præcist, hvordan vi vurderer og rangerer casinoer."
        breadcrumb={<><Link to="/">Forside</Link> › Vores Metode</>}
      />

      <section className="method-intro section">
        <div className="container">
          <div className="method-intro-grid">
            <div>
              <div className="divider"></div>
              <h2>En metode bygget på integritet</h2>
              <p>
                Vores anmeldelsesmetode er udviklet med ét mål for øje: at give danske spillere de mest pålidelige,
                gennemsigtige og nyttige vurderinger af online casinoer på det regulerede danske marked.
              </p>
              <p>
                Vi anmelder ikke casinoer for at tjene hurtige kommissioner. Vi anmelder dem for at hjælpe spillere
                med at træffe informerede beslutninger – og for at holde industrien ansvarlig over for spillerne.
              </p>
              <p>
                Vores vurderingsmodel er opdelt i fem overordnede kategorier, der tilsammen udgør en samlet score.
                Hvert casino gennemgås selvstændigt og tildeles en karakter fra 1-10 i hver kategori.
              </p>
            </div>
            <div className="method-score-overview">
              <h4>Vores vægtningsmodel</h4>
              {criteria.map((c) => (
                <div key={c.num} className="method-weight-bar">
                  <div className="method-weight-info">
                    <span className="method-weight-icon">{c.icon}</span>
                    <span className="method-weight-title">{c.title}</span>
                    <span className="method-weight-pct">{c.weight}</span>
                  </div>
                  <div className="method-weight-track">
                    <div
                      className="method-weight-fill"
                      style={{ width: c.weight }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed criteria */}
      <section className="method-criteria section bg-sand">
        <div className="container">
          <div className="divider"></div>
          <h2 style={{ marginBottom: '0.5rem' }}>Detaljerede vurderingskriterier</h2>
          <p className="section-subtitle" style={{ marginBottom: '3rem' }}>
            Nedenfor gennemgår vi hvert kriterium i detalje, så du præcist kan forstå, hvad vi kigger efter.
          </p>

          <div className="criteria-list">
            {criteria.map((c) => (
              <div key={c.num} className="criteria-item">
                <div className="criteria-header">
                  <div className="criteria-meta">
                    <span className="criteria-num">{c.num}</span>
                    <span className="criteria-icon">{c.icon}</span>
                    <div>
                      <h3>{c.title}</h3>
                      <span className="criteria-weight-badge">{c.weight} af samlet vurdering</span>
                    </div>
                  </div>
                </div>
                <div className="criteria-body">
                  <p>{c.desc}</p>
                  <ul className="criteria-points">
                    {c.points.map((pt, i) => (
                      <li key={i}>
                        <span className="criteria-check">✓</span>
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process steps */}
      <section className="method-process section">
        <div className="container">
          <div className="divider"></div>
          <h2 style={{ marginBottom: '2.5rem' }}>Vores anmeldelsesproces trin for trin</h2>

          <div className="process-steps">
            <div className="process-step">
              <div className="process-step-num">1</div>
              <div className="process-step-content">
                <h4>Licensverifikation</h4>
                <p>Vi starter altid med at verificere, at casinoet har en gyldig og aktiv licens fra Spillemyndigheden. Vi tjekker det officielle register og afviser uautoriserede udbydere.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-num">2</div>
              <div className="process-step-content">
                <h4>Registrering og test</h4>
                <p>Vi opretter en testkonto og gennemgår tilmeldingsprocessen, bonus-vilkår og velkomstoplevelsen som en ny bruger ville gøre det.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-num">3</div>
              <div className="process-step-content">
                <h4>Spil og platformstest</h4>
                <p>Vi tester et repræsentativt udsnit af casinoets spil på tværs af platforme (desktop og mobil) og vurderer spiloplevelse og teknisk stabilitet.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-num">4</div>
              <div className="process-step-content">
                <h4>Betalingstest</h4>
                <p>Vi foretager test-transaktioner for at vurdere ind- og udbetalingsprocessen, herunder hastighed, metoder og eventuelle gebyrer.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-num">5</div>
              <div className="process-step-content">
                <h4>Supporttest</h4>
                <p>Vi kontakter casinoets kundesupport via live chat og e-mail med realistiske spørgsmål og evaluerer svartid, kompetence og tone.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="process-step-num">6</div>
              <div className="process-step-content">
                <h4>Scoring og publicering</h4>
                <p>Vi beregner den samlede score ud fra vores vægtningsmodel, skriver en uafhængig anmeldelse og publicerer resultatet med fuld transparens.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Independence statement */}
      <section className="method-independence section-sm bg-sand">
        <div className="container">
          <div className="independence-box">
            <h3>Vores uafhængihedserklæring</h3>
            <p>
              Ingen casino-operatør har haft indflydelse på indholdet af vores anmeldelser. Vi modtager ikke betaling
              for positive anmeldelser. Vores affiliate-kommissioner modtages udelukkende, når en bruger følger et
              af vores links og opretter en konto – dette sker <em>efter</em> vores redaktionelle vurdering er afsluttet
              og er aldrig en forudsætning for omtale.
            </p>
            <p>
              Casinoer kan kontakte os for at blive inkluderet i vores database, men inkludering afhænger
              udelukkende af, at casinoet opfylder vores minimumskrav (dansk licens) og gennemgår vores
              fulde vurderingsproces.
            </p>
            <Link to="/om-os" className="btn btn-outline" style={{ marginTop: '1rem' }}>
              Læs mere om vores redaktion
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
